/** Team page strings, verbatim from the live captures and Clockify's string table (team spec 3-6, 16). */
export const s = {
  title: "Team",
  tabMembers: "Members",
  tabFull: "Full",
  tabLimited: "Limited",
  tabGroups: "Groups",
  tabReminders: "Reminders",

  addNewMember: "Add new member",
  addFullMember: "Add full member",
  addLimitedMember: "Add limited member",
  filter: "Filter",
  statusAll: "All",
  statusActive: "Active",
  statusInactive: "Inactive",
  statusInvited: "Invited",
  showAll: "Show all",
  showActive: "Show active",
  showInactive: "Show inactive",
  showInvited: "Show invited",
  role: "Role",
  group: "Group",
  searchGroup: "Search Group",
  noGroup: "No Group",
  searchByNameOrEmail: "Search by name or email",
  searchByName: "Search by name",
  applyFilter: "Apply filter",
  clearFilters: "Clear filters",
  membersHeader: "Members",
  limitedHeader: "Limited",
  export: "Export",
  saveAsCsv: "Save as CSV",
  saveAsExcel: "Save as Excel",
  downloadFailed: "Download failed",

  colName: "Name",
  colEmail: "Email",
  colRole: "Role",
  colGroup: "Group",
  colBillableRate: (cur: string) => `Billable rate (${cur})`,
  colCostRate: (cur: string) => `Cost rate (${cur})`,
  colActions: "Actions",
  you: " (you)",
  notJoinedYet: "(not joined yet)",
  invitedTag: "Invited",
  inactiveTag: "Inactive",
  addGroupLink: "+ Group",
  addRoleLink: "+ Role",
  change: "Change",
  dash: "–",
  noResults: "No results",
  noResultsHint: "Try adjusting the filters to get some results.",
  loading: "Loading",

  roleOwner: "Owner",
  roleAdmin: "Admin",
  roleTeamManager: "Team manager",
  roleProjectManager: "Project manager",
  roleMember: "Member",

  rowMenu: (name: string) => `Actions for ${name}`,
  editProfile: "Edit profile",
  deactivate: "Deactivate",
  activate: "Activate",
  transferOwnership: "Transfer ownership",
  deleteUser: "Delete user",
  remove: "Remove",
  resendInvite: "Resend an invite email",
  copyInviteLink: "Copy invite link",
  revokeInvitation: "Revoke invitation",
  ownerLocked: "Can't be removed or deactivated until ownership is transferred",

  userActivated: "User activated",
  userDeactivated: "User deactivated",
  usersActivated: (n: number) => `${n} users activated`,
  usersDeactivated: (n: number) => `${n} users deactivated`,
  limitedActivated: "Limited user activated",
  limitedDeactivated: "Limited user deactivated",
  inviteResent: "Invite sent",
  linkCopied: "Invite link copied",
  inviteLinksHint: "Email isn't configured. Share these invite links:",
  invitationSent: "Invitation sent",

  deactivateTitle: "Deactivate",
  deactivateBody: (name: string) =>
    `${name} will lose access to this workspace, but their tracked data will remain. You can activate them again anytime.`,
  removeTitle: "Remove user",
  removeBody:
    "The user will lose access to this workspace, but their tracked data will remain. This action can't be reversed.",
  deleteLimitedTitle: "Delete user",
  deleteLimitedBody:
    "Limited user won’t be able to clock in via kiosk, but their tracked data will remain. This action can't be reversed.",
  revokeTitle: "Revoke invitation",
  revokeBody: (email: string) =>
    `Once the invite is revoked, the user with email ${email} will no longer have access to the workspace and will require a new invite to join.`,
  revoke: "Revoke",
  cancel: "Cancel",
  save: "Save",
  add: "Add",
  delete: "Delete",

  // Bulk
  selected: (n: number) => `${n} selected`,
  clearSelection: "Clear selection",
  selectAllUsers: (n: number) => `Select all ${n} users`,
  allUsersSelected: (n: number) => `All ${n} users are selected`,
  bulkEdit: "Bulk edit",
  bulkEditTitle: (n: number) => (n === 1 ? "Bulk edit 1 user" : `Bulk edit ${n} users`),
  bulkUpdated: (n: number) => (n === 1 ? "Successfully updated 1 user" : `Successfully updated ${n} users`),
  selectDay: "Select day",
  selectDays: "Select days",
  enterHours: "Enter hours",
  hoursPerDay: "hours per day",
  billableRateLabel: "Billable rate:",
  costRateLabel: "Cost rate",
  addToExisting: "Add to existing",
  overwriteExisting: "Overwrite existing",

  // Invite modal
  addMembersTitle: "Add members",
  addFullMembersTitle: "Add full members",
  inviteByEmail: "Invite by email",
  inviteAlert: "Add multiple members at once by separating emails with a comma",
  inviteHelp: "Separate multiple emails with commas, spaces, or semicolons.",
  invitePlaceholder: "Enter one or more email addresses",
  insertNames: "Insert names",
  insertNamesTip: "Include names with each member invite.",
  enterFullName: "Enter full name",
  sendInvite: "Send invite",
  invalidEmail: (v: string) => `${v} is not a valid email.`,
  invalidEmails: (v: string) => `${v} are not valid emails.`,
  removeInvalid: "Remove all invalid emails",
  duplicateEmail: "You have already entered this email address.",
  tooManyInvites: (n: number) => `Can’t add more than ${n} members at once.`,
  discardTitle: "Discard adding new members?",
  discard: "Discard",
  removeEmail: (e: string) => `Remove ${e}`,

  // Limited
  addLimitedTitle: "Add limited members",
  employeeName: "Employee name",
  limitedPlaceholder: "Enter one or more names",
  limitedTip: "Separate multiple names with enter.",
  noLimitedTitle: "No limited members yet",
  noLimitedBody: "Add limited members to clock in and out via kiosk.",
  limitedFootnote: "Limited members can only clock in and out via kiosk.",

  // Role modal
  roleTitle: (name: string) => `${name}'s role`,
  memberDesc: "Can track time on assigned projects. Default role for all users.",
  adminDesc: "Can see and edit everything. Only an Owner can remove an Admin role.",
  pmDesc: "Can edit all projects they manage, and see and approve time entries on those projects.",
  tmDesc: "Can see time entries of users they manage and approve their timesheets.",
  selectUser: "Select user",
  pmProjects: "Managed projects",
  pmNone: "Assign projects on the project's Access tab.",
  adminConfirmTitle: "Change user role to admin",
  adminConfirmBody: "This grants the user administrative privileges.",
  changeBtn: "Change",
  rolesSaved: "Role updated",

  // Picker (MemberGroupPicker)
  searchUsersOrGroups: "Search users or groups",
  show: "Show",
  selectAll: "Select all",
  users: "Users",
  groups: "Groups",
  withoutGroup: "Without group",
  everyone: "Everyone",

  // Edit profile
  editProfileTitle: "Edit profile",
  credentialsCaption: "User log-in credentials and the name that is displayed in reports.",
  weekStart: "Week start",
  weekStartCaption: "Change when the user's week starts.",
  weekStartLocked: "Start of the week can’t be changed due to pending approvals.",
  workingDays: "Working days",
  workingDaysCaption: "Define on which days this user works.",
  mustHaveDay: "Must have at least one working day",
  capacity: "Daily work capacity",
  capacityCaption: "Enter the daily working time capacity.",
  ownerCapacity: "Can't edit owner's work capacity.",
  assignedTm: "Assigned team manager",
  assignedTmCaption: "Add team manager to view this user's time and approve timesheets.",
  selectTm: "Select team manager",
  profileUpdated: "Profile was updated successfully",
  pendingProfile: "Can't edit profile until the user accepts the invite",
  inactiveProfile: "Can’t edit inactive user’s profile",

  // Rate
  billableRateInput: "Billable rate input",
  costRateInput: "Cost rate input",
  applyBillable: "Apply this billable rate to:",
  applyCost: "Apply this cost rate to:",
  fromDate: "Time entries from",
  onwards: "onwards",
  allEntries: "All past and future time entries",
  rateTitle: "Billable rate",
  costRateTitle: "Cost rate",

  // Transfer
  transferTitle: "Transfer owner role",
  transferBody: (name: string) =>
    `You'll be downgraded to admin and will no longer be in charge of the workspace. Are you sure you want to transfer your owner role to ${name}?`,
  transferInput: "Type TRANSFER to confirm",
  transfer: "Transfer",
  transferred: "Owner role transferred successfully",
  notTransferred: "Owner role not transferred",

  // Groups
  noGroupsTitle: "No user groups yet",
  noGroupsBody: "Add a group to simplify management and tracking.",
  enterGroupName: "Enter group name",
  addNewGroup: "Add new group",
  searchGroups: "Search by username or group name",
  groupsHeader: "Groups",
  colAccess: "Access",
  colTeamManager: "Team manager",
  addAccess: "+ Access",
  addManager: "+ Team manager",
  editGroup: "Edit group",
  editGroupLabel: (n: string) => `Edit ${n}`,
  deleteGroupLabel: (n: string) => `Delete ${n}`,
  deleteGroupBody: (n: string) => `Are you sure you want to delete group ${n}?`,
  groupName: "Group name",
  addSearchGroups: "Add/Search groups",
  findUser: "Find user or group",

  // Reminders
  noRemindersTitle: "No reminders yet",
  noRemindersBody: "Create reminders to keep work on track.",
  tokIf: "If",
  tokSelectMembers: "(select team members)",
  tokTracks: "tracks",
  tokLess: "Less",
  tokMore: "More",
  tokThan: "than",
  tokCapacity: "Their work capacity",
  tokCapacitySub: "Targets automatically calculated.",
  tokCustom: "Custom time",
  tokCustomSub: "Freely define targets.",
  tokHours: "hours",
  tokOnA: "on a",
  tokOn: "on",
  tokSelectDays: "(select days)",
  tokDaily: "Daily",
  tokWeekly: "Weekly",
  tokMonthly: "Monthly",
  tokBasisSend: "basis, send",
  tokBasis: "basis",
  tokSend: ", send",
  tokThem: "Them",
  tokManagers: "Their managers",
  tokAdmins: "All admins",
  tokAReminder: "a reminder",
  reminderNoMembers: "To add a reminder select at least one team member",
  reminderNoDays: "To update the reminder select at least one week day",
  reminderNotUpdated: "Reminder not updated",
  inactiveNoReminders: "Inactive users don't receive reminders.",
  deleteReminderTitle: "Delete reminder",
  deleteReminderBody: "Deleting this reminder will permanently remove it from the system.",
  deleteReminderLabel: "Delete reminder",
  hoursInput: "Hours",

  weekdays: {
    MONDAY: "Monday",
    TUESDAY: "Tuesday",
    WEDNESDAY: "Wednesday",
    THURSDAY: "Thursday",
    FRIDAY: "Friday",
    SATURDAY: "Saturday",
    SUNDAY: "Sunday",
  },
  weekdaysShort: {
    MONDAY: "Mon",
    TUESDAY: "Tue",
    WEDNESDAY: "Wed",
    THURSDAY: "Thu",
    FRIDAY: "Fri",
    SATURDAY: "Sat",
    SUNDAY: "Sun",
  },
} as const;
